<template>
  <div class="wrapper">
    <!-- Page Header -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Payroll</h1>
            <p class="text-muted mb-0">
              Pay your employees and keep EPF, SOCSO and EIS up to date
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshTable">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add payroll
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Dashboard Metrics (org-wide, real) -->
    <CRow class="mb-4">
      <CCol lg="3" md="6" class="mb-3 mb-lg-0">
        <div class="inv-kpi">
          <div class="inv-kpi-top">
            <span class="inv-kpi-label">Total payrolls</span>
            <span class="inv-kpi-chip chip-navy"><CIcon name="cil-list" /></span>
          </div>
          <div class="inv-kpi-value">{{ payrollSummary.totalCount }}</div>
          <div class="inv-kpi-sub">{{ payrollSummary.draftCount }} draft</div>
        </div>
      </CCol>
      <CCol lg="3" md="6" class="mb-3 mb-lg-0">
        <div class="inv-kpi">
          <div class="inv-kpi-top">
            <span class="inv-kpi-label">Total net pay</span>
            <span class="inv-kpi-chip chip-navy"><CIcon name="cil-money" /></span>
          </div>
          <div class="inv-kpi-value">{{ money(payrollSummary.totalNetPay) }}</div>
          <div class="inv-kpi-sub">across all records</div>
        </div>
      </CCol>
      <CCol lg="3" md="6" class="mb-3 mb-lg-0">
        <div class="inv-kpi">
          <div class="inv-kpi-top">
            <span class="inv-kpi-label">Paid</span>
            <span class="inv-kpi-chip chip-success"><CIcon name="cil-check-circle" /></span>
          </div>
          <div class="inv-kpi-value">{{ money(payrollSummary.paidAmount) }}</div>
          <div class="inv-kpi-sub">{{ payrollSummary.paidCount }} payroll{{ payrollSummary.paidCount === 1 ? '' : 's' }}</div>
        </div>
      </CCol>
      <CCol lg="3" md="6">
        <div class="inv-kpi">
          <div class="inv-kpi-top">
            <span class="inv-kpi-label">Pending</span>
            <span class="inv-kpi-chip chip-warning"><CIcon name="cil-clock" /></span>
          </div>
          <div class="inv-kpi-value">{{ money(payrollSummary.pendingAmount) }}</div>
          <div class="inv-kpi-sub">{{ payrollSummary.pendingCount }} payroll{{ payrollSummary.pendingCount === 1 ? '' : 's' }}</div>
        </div>
      </CCol>
    </CRow>

    <!-- Advanced Filters -->
    <CCollapse :show="showFilters">
      <CCard class="mb-4 border">
        <CCardBody>
          <CRow>
            <CCol md="3" class="mb-3 mb-md-0">
              <CInput
                label="Employee name"
                v-model="filters.employeeName"
                placeholder="Search by name"
                @keyup.enter="applyFilters"
              />
            </CCol>
            <CCol md="3" class="mb-3 mb-md-0">
              <CInput
                label="Payroll number"
                v-model="filters.payrollNo"
                placeholder="Search by payroll #"
                @keyup.enter="applyFilters"
              />
            </CCol>
            <CCol md="2" class="mb-3 mb-md-0">
              <CInput
                label="From"
                type="date"
                v-model="filters.dateFrom"
                @change="applyFilters"
              />
            </CCol>
            <CCol md="2" class="mb-3 mb-md-0">
              <CInput
                label="To"
                type="date"
                v-model="filters.dateTo"
                @change="applyFilters"
              />
            </CCol>
            <CCol md="2">
              <CSelect
                label="Status"
                v-model="filters.status"
                :options="statusOptions"
                @change="applyFilters"
              />
            </CCol>
          </CRow>
          <CRow class="mt-3">
            <CCol class="d-flex justify-content-end">
              <CButton color="light" size="sm" @click="resetFilters">
                <CIcon name="cil-x-circle" /> Clear filters
              </CButton>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </CCollapse>

    <!-- Toolbar -->
    <CRow class="mb-4 align-items-center">
      <CCol xs="12" md="6" class="mb-3 mb-md-0">
        <CButtonGroup>
          <CButton color="light" @click="showFilters = !showFilters">
            <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
            {{ showFilters ? "Hide filters" : "Show filters" }}
          </CButton>
          <CBadge v-if="hasActiveFilters" color="primary" class="ml-2 align-self-center">
            {{ activeFiltersCount }} active
          </CBadge>
        </CButtonGroup>
      </CCol>
    </CRow>

    <!-- Main Table -->
    <CRow>
      <CCol sm="12">
        <CCard class="border-0">
          <!-- First load -->
          <CCardBody v-if="loading && !items.length" class="text-center py-5">
            <CSpinner color="primary" />
            <p class="text-muted mt-3 mb-0">Loading payroll…</p>
          </CCardBody>

          <!-- Error -->
          <CCardBody v-else-if="loadError && !items.length" class="text-center py-5">
            <CIcon name="cil-warning" size="3xl" class="mb-3 text-danger" />
            <h2 class="h5">Couldn't load payroll</h2>
            <p class="text-muted mb-3">Check your connection and try again.</p>
            <CButton color="primary" @click="refreshTable">
              <CIcon name="cil-reload" /> Try again
            </CButton>
          </CCardBody>

          <!-- Empty (first use) -->
          <CCardBody v-else-if="!items.length" class="text-center py-5">
            <CIcon name="cil-money" size="3xl" class="mb-3 text-muted" />
            <h2 class="h5">No payroll yet</h2>
            <p class="text-muted mb-3">Add a payroll to pay an employee and work out EPF, SOCSO and EIS.</p>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add payroll
            </CButton>
          </CCardBody>

          <!-- Empty (filtered) -->
          <CCardBody v-else-if="!computedItems.length" class="text-center py-5">
            <CIcon name="cil-filter" size="3xl" class="mb-3 text-muted" />
            <h2 class="h5">No payroll matches your filters</h2>
            <CButton color="light" class="mt-2" @click="resetFilters">
              <CIcon name="cil-x" /> Clear filters
            </CButton>
          </CCardBody>

          <template v-else>
            <CProgress v-if="loading" :value="100" animated color="primary" size="xs" />
            <CCardBody class="d-none d-lg-block">
              <CDataTable
                :items="computedItems"
                :fields="fields"
                items-per-page-select
                :items-per-page="25"
                hover
                sorter
                :sorter-value="{ column: 'date', asc: false }"
                pagination
                responsive
              >
                <template #show_index="{ index }">
                  <td class="py-2">
                    {{ index + 1 }}
                  </td>
                </template>

                <template #profileName="{ item }">
                  <td class="py-2">
                    <router-link :to="`/tenants/Payroll/${item.id}`" class="font-weight-bold">
                      {{ item.profileName }}
                    </router-link>
                    <small class="d-block text-muted">{{ item.profile && item.profile.email }}</small>
                  </td>
                </template>

                <template #date="{ item }">
                  <td class="py-2">{{ formatDate(item.date) }}</td>
                </template>

                <template #payrollNo="{ item }">
                  <td class="py-2 text-monospace">{{ item.payrollNo }}</td>
                </template>

                <template #grossAmount="{ item }">
                  <td class="py-2 text-right">{{ money(item.grossAmount) }}</td>
                </template>

                <template #totalDeductions="{ item }">
                  <td class="py-2 text-right">{{ money(item.totalDeductions) }}</td>
                </template>

                <template #netAmount="{ item }">
                  <td class="py-2 text-right font-weight-bold">{{ money(item.netAmount) }}</td>
                </template>

                <template #statusDescription="{ item }">
                  <td class="py-2">
                    <CBadge :color="hrStatusColor('payroll', item.statusDescription)">
                      {{ hrStatusLabel('payroll', item.statusDescription) }}
                    </CBadge>
                  </td>
                </template>

                <template #show_details="{ item }">
                  <td class="py-2">
                    <div class="text-right action-buttons">
                      <CButtonGroup>
                        <CButton color="light" size="sm" class="border" :aria-label="`Edit payroll ${item.payrollNo || ''}`" title="Edit" @click="onEdit(item)">
                          <CIcon name="cil-pencil" />
                        </CButton>
                        <CDropdown size="sm" color="light" placement="bottom-end" class="dropdown-with-zindex border">
                          <template #toggler-content>
                            <CIcon name="cil-options" />
                            <span class="sr-only">More actions</span>
                          </template>
                          <CDropdownItem @click="onEdit(item)">
                            <CIcon name="cil-pencil" class="mr-2" /> Edit payroll
                          </CDropdownItem>
                          <CDropdownItem @click="onDuplicate(item)">
                            <CIcon name="cil-copy" class="mr-2" /> Duplicate
                          </CDropdownItem>
                          <CDropdownItem v-if="hasPayslip(item)" @click="viewPayslip(item)">
                            <CIcon name="cil-file" class="mr-2" /> View payslip
                          </CDropdownItem>
                          <CDropdownDivider v-if="canGenerateExpense(item)" />
                          <CDropdownItem
                            v-if="canGenerateExpense(item)"
                            @click="generateExpenseForContributions(item)"
                            :disabled="item.expenseGenerated"
                          >
                            <CIcon name="cil-dollar" class="mr-2" />
                            {{ item.expenseGenerated ? 'Expense already created' : 'Create employer contribution expense' }}
                          </CDropdownItem>
                          <CDropdownDivider />
                          <CDropdownItem @click="showDeleteConfirmation(item)" class="text-danger">
                            <CIcon name="cil-trash" class="mr-2" /> Delete
                          </CDropdownItem>
                        </CDropdown>
                      </CButtonGroup>
                    </div>
                  </td>
                </template>
              </CDataTable>
            </CCardBody>

            <!-- Mobile View -->
            <CCardBody class="d-lg-none">
              <CCard v-for="item in computedItems" :key="item.id" class="mb-3 border">
                <CCardHeader class="d-flex justify-content-between align-items-center py-3">
                  <div>
                    <router-link :to="`/tenants/Payroll/${item.id}`" class="font-weight-bold">{{ item.profileName }}</router-link>
                    <small class="d-block text-muted text-monospace">{{ item.payrollNo }}</small>
                  </div>
                  <CBadge :color="hrStatusColor('payroll', item.statusDescription)">
                    {{ hrStatusLabel('payroll', item.statusDescription) }}
                  </CBadge>
                </CCardHeader>
                <CCardBody class="py-3">
                  <CRow>
                    <CCol xs="6">
                      <div class="mb-2">
                        <div class="text-muted small">Date</div>
                        <div>{{ formatDate(item.date) }}</div>
                      </div>
                      <div class="mb-2">
                        <div class="text-muted small">Gross pay</div>
                        <div>{{ money(item.grossAmount) }}</div>
                      </div>
                    </CCol>
                    <CCol xs="6" class="text-right">
                      <div class="mb-2">
                        <div class="text-muted small">Deductions</div>
                        <div>{{ money(item.totalDeductions) }}</div>
                      </div>
                      <div class="mb-2">
                        <div class="text-muted small">Net pay</div>
                        <div class="font-weight-bold">{{ money(item.netAmount) }}</div>
                      </div>
                    </CCol>
                  </CRow>
                </CCardBody>
                <CCardFooter class="py-2 d-flex justify-content-between align-items-center">
                  <CButton color="light" @click="onEdit(item)">
                    <CIcon name="cil-pencil" /> Edit
                  </CButton>
                  <CDropdown color="light" placement="bottom-end">
                    <template #toggler-content>
                      <CIcon name="cil-options" />
                      <span class="sr-only">More actions</span>
                    </template>
                    <CDropdownItem @click="onDuplicate(item)">
                      <CIcon name="cil-copy" class="mr-2" /> Duplicate
                    </CDropdownItem>
                    <CDropdownItem v-if="hasPayslip(item)" @click="viewPayslip(item)">
                      <CIcon name="cil-file" class="mr-2" /> View payslip
                    </CDropdownItem>
                    <CDropdownItem
                      v-if="canGenerateExpense(item)"
                      @click="generateExpenseForContributions(item)"
                      :disabled="item.expenseGenerated"
                    >
                      <CIcon name="cil-dollar" class="mr-2" />
                      {{ item.expenseGenerated ? 'Expense already created' : 'Create employer contribution expense' }}
                    </CDropdownItem>
                    <CDropdownDivider />
                    <CDropdownItem @click="showDeleteConfirmation(item)" class="text-danger">
                      <CIcon name="cil-trash" class="mr-2" /> Delete
                    </CDropdownItem>
                  </CDropdown>
                </CCardFooter>
              </CCard>
            </CCardBody>
          </template>
        </CCard>
      </CCol>
    </CRow>

    <!-- Delete Confirmation Modal -->
    <CModal
      :title="`Delete payroll ${itemToDelete.payrollNo || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        The payroll for <strong>{{ itemToDelete.profileName }}</strong>
        dated {{ formatDate(itemToDelete.date) }} (net pay {{ money(itemToDelete.netAmount) }})
        will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" /> Delete payroll
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import PayrollApi from "@/lib/payrollApi";
import PaySlipApi from "@/lib/paySlipApi";
import { formatMyr } from "@/utils/money";
import { formatDate, asDateString } from "@/utils/datetime";
import { hrStatusMixin, hrStatusOptions } from "@/utils/hrStatusBadges";

const items = [];
const fields = [
  {
    key: "show_index",
    label: "#",
    _style: "width:50px",
    sorter: false,
    filter: false,
  },
  {
    key: "profileName",
    label: "Employee",
    sorter: true,
    filter: true,
    _style: "min-width:200px"
  },
  {
    key: "date",
    label: "Date",
    sorter: true,
    filter: false,
    _style: "width:150px"
  },
  {
    key: "payrollNo",
    label: "Payroll no.",
    sorter: true,
    filter: true,
    _style: "width:120px"
  },
  {
    key: "reference",
    label: "Reference",
    sorter: true,
    filter: false,
    _style: "width:130px"
  },
  {
    key: "grossAmount",
    label: "Gross pay",
    sorter: true,
    filter: false,
    _style: "text-align:right; width:130px"
  },
  {
    key: "totalDeductions",
    label: "Deductions",
    sorter: true,
    filter: false,
    _style: "text-align:right; width:120px"
  },
  {
    key: "netAmount",
    label: "Net pay",
    sorter: true,
    filter: false,
    _style: "text-align:right; width:130px"
  },
  {
    key: "statusDescription",
    label: "Status",
    sorter: true,
    filter: true,
    _style: "width:110px"
  },
  {
    key: "show_details",
    label: "Actions",
    _style: "width:120px",
    sorter: false,
    filter: false,
  },
];

export default {
  name: "PayrollList",
  mixins: [hrStatusMixin],
  data() {
    return {
      loading: true,
      items: items.map((item, id) => {
        return { ...item, id };
      }),
      loadError: false,
      deleting: false,
      paySlipApi: new PaySlipApi(),
      payslips: [],
      fields,
      api: new PayrollApi(),
      warningModal: false,
      itemToDelete: {},
      showFilters: false,
      filters: {
        employeeName: "",
        payrollNo: "",
        dateFrom: "",
        dateTo: "",
        status: "",
      },
      statusOptions: hrStatusOptions("payroll"),

      // Real analytics source — org-wide payrolls for the KPI summary.
      // (The table renders from `items`; KPIs read this separate pull.)
      allPayrolls: [],
    };
  },
  computed: {
    computedItems() {
      let filtered = this.items.map((item) => {
        return {
          ...item,
          profileName: item.profile?.fullName || item.profileName,
        };
      });

      // Apply filters
      if (this.filters.employeeName) {
        const search = this.filters.employeeName.toLowerCase();
        filtered = filtered.filter(item =>
          item.profileName?.toLowerCase().includes(search)
        );
      }
      if (this.filters.payrollNo) {
        const search = this.filters.payrollNo.toLowerCase();
        filtered = filtered.filter(item =>
          item.payrollNo?.toLowerCase().includes(search)
        );
      }
      if (this.filters.status) {
        filtered = filtered.filter(item =>
          item.statusDescription === this.filters.status
        );
      }
      if (this.filters.dateFrom) {
        filtered = filtered.filter(item =>
          asDateString(item.date) >= this.filters.dateFrom
        );
      }
      if (this.filters.dateTo) {
        filtered = filtered.filter(item =>
          asDateString(item.date) <= this.filters.dateTo
        );
      }

      return filtered;
    },
    totalItems() {
      return this.computedItems.length;
    },

    // ===== Real org-wide analytics (computed from allPayrolls) =====
    payrollSummary() {
      const all = this.allPayrolls || [];
      const num = (v) => parseFloat(v) || 0;
      let totalNetPay = 0, paidAmount = 0, paidCount = 0, pendingAmount = 0, pendingCount = 0, draftCount = 0;

      all.forEach((p) => {
        const net = num(p.netAmount);
        totalNetPay += net;
        const status = p.statusDescription;
        if (status === 'Paid') {
          paidAmount += net;
          paidCount++;
        } else if (status === 'Draft') {
          draftCount++;
        } else if (status === 'Cancelled') {
          // excluded from pending totals
        } else {
          // Approve / Approved / Pending — approved & awaiting payment
          pendingAmount += net;
          pendingCount++;
        }
      });

      return {
        totalCount: all.length,
        totalNetPay,
        paidAmount,
        paidCount,
        pendingAmount,
        pendingCount,
        draftCount,
      };
    },

    hasActiveFilters() {
      return Object.values(this.filters).some(filter => filter !== "");
    },
    activeFiltersCount() {
      return Object.values(this.filters).filter(filter => filter !== "").length;
    },
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Staff", "Payroll"]);
    // EmployeeList links here with ?employee=<name> for "Payroll history"
    if (this.$route.query.employee) {
      this.filters.employeeName = String(this.$route.query.employee);
      this.showFilters = true;
    }
    var self = this;
    self.refreshTable();
    self.fetchSummary();
    self.fetchPayslips();
  },
  methods: {
    formatDate,
    money(amount) {
      return `RM ${formatMyr(amount)}`;
    },
    applyFilters() {
      // Filters are applied instantly via the computed property
    },
    resetFilters() {
      this.filters = {
        employeeName: "",
        payrollNo: "",
        dateFrom: "",
        dateTo: "",
        status: "",
      };
    },
    // Payslips are generated when a payroll is approved; they carry the same
    // employee, date and reference as the payroll they came from.
    findPayslip(item) {
      const day = asDateString(item.date);
      return this.payslips.find(
        (p) =>
          (p.profileId || (p.profile && p.profile.id)) === item.profileId &&
          asDateString(p.date) === day &&
          (p.reference || "") === (item.reference || "")
      );
    },
    hasPayslip(item) {
      return !!this.findPayslip(item);
    },
    viewPayslip(item) {
      const payslip = this.findPayslip(item);
      if (payslip) this.$router.push({ path: `/tenants/paySlip/${payslip.id}` });
    },
    fetchPayslips() {
      this.paySlipApi
        .getListByCurrentBusiness()
        .then((r) => {
          this.payslips = r.result || [];
        })
        .catch(() => {
          // Without payslips the "View payslip" action is simply hidden.
          this.payslips = [];
        });
    },
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning" }[color] || "info";
      this.$toast[kind](message);
    },
    refreshTable() {
      var self = this;
      self.loading = true;
      self.loadError = false;
      self.api
        .getListByCurrentBusiness()
        .then((response) => {
          self.items = response.result;
          self.loading = false;
        })
        .catch((err) => {
          console.error("Couldn't load payroll", err);
          self.loadError = true;
          self.loading = false;
          if (self.items.length) {
            self.toast("Error", "Couldn't refresh payroll. Check your connection and try again.", "danger");
          }
        });
    },

    // Org-wide pull for the real KPI summary (payroll endpoint returns a RAW ARRAY).
    fetchSummary() {
      var self = this;
      self.api
        .getListByCurrentBusiness()
        .then((r) => {
          self.allPayrolls = (r.result?.data ?? r.data ?? r.result) || [];
        })
        .catch(() => { /* analytics degrade gracefully; the table still works */ });
    },
    onEdit(item) {
      var self = this;
      self.$router.push({
        path: `/tenants/Payroll/${item.id}`,
      });
    },
    onDuplicate(item) {
      var self = this;
      self.$router.push({
        path: `/tenants/Payroll/${item.id}/duplicate`,
      });
    },
    confirmDelete() {
      var self = this;
      self.deleting = true;
      this.api
        .delete(self.itemToDelete.id)
        .then(() => {
          self.toast("Success", "Payroll deleted", "success");
          self.warningModal = false;
          self.itemToDelete = {};
          self.refreshTable();
          self.fetchSummary();
        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't delete the payroll. ${helper.getErrorMessage(data)}`, "danger");
        })
        .finally(() => {
          self.deleting = false;
        });
    },
    showDeleteConfirmation(item) {
      var self = this;
      self.itemToDelete = item;
      self.warningModal = true;
    },
    addNew() {
      this.$router.push({ path: "/tenants/Payroll" });
    },
    canGenerateExpense(item) {
      // Only show for Approved or Paid payrolls
      return item.statusDescription === 'Approve' || item.statusDescription === 'Paid';
    },
    async generateExpenseForContributions(item) {
      const self = this;

      try {
        self.loading = true;

        // Fetch full payroll details with employer contributions
        const response = await self.api.get(item.id);
        const payroll = response.result;

        if (!payroll.employerContributions || payroll.employerContributions.length === 0) {
          self.toast("Warning", "This payroll has no employer contributions to record", "warning");
          self.loading = false;
          return;
        }

        // Group contributions by type (EPF, SOCSO, EIS)
        // Combine employee deductions + employer contributions for each type
        const contributionTypes = [
          { type: 0, name: 'EPF' },
          { type: 1, name: 'SOCSO' },
          { type: 2, name: 'EIS' }
        ];

        const expenseEntries = [];

        contributionTypes.forEach(({ type, name }) => {
          // Find employee deduction for this type
          const employeeDeduction = payroll.deductions?.find(d => d.type === type);
          const employeeAmount = employeeDeduction ? parseFloat(employeeDeduction.amount) || 0 : 0;

          // Find employer contribution for this type
          const employerContribution = payroll.employerContributions.find(c => c.type === type);
          const employerAmount = employerContribution ? parseFloat(employerContribution.amount) || 0 : 0;

          // Only create entry if there's a contribution
          if (employeeAmount > 0 || employerAmount > 0) {
            const totalAmount = employeeAmount + employerAmount;

            expenseEntries.push({
              source: 'payroll',
              payrollId: payroll.id,
              payrollNo: payroll.payrollNo,
              employeeName: payroll.profile?.fullName || item.profileName,
              date: payroll.date,
              totalAmount: totalAmount,
              employeeAmount: employeeAmount,
              employerAmount: employerAmount,
              description: `${name} (Employee: ${self.formatCurrency(employeeAmount)} + Employer: ${self.formatCurrency(employerAmount)}) - ${payroll.profile?.fullName} - ${payroll.payrollNo}`,
              chartAccountId: employerContribution?.chartAccountId || null,
              contributionType: name
            });
          }
        });

        // Store all expense entries in session storage
        sessionStorage.setItem('pendingPayrollExpenses', JSON.stringify(expenseEntries));

        // Navigate to Other Expense List page where modal will show
        self.$router.push({
          path: '/tenants/otherExpenseList',
          query: { fromPayroll: 'true', count: expenseEntries.length }
        });

        self.toast("Success", `${expenseEntries.length} expense ${expenseEntries.length === 1 ? "entry" : "entries"} ready to review`, "success");

      } catch (error) {
        console.error('Error generating expense:', error);
        self.toast("Error", "Couldn't prepare the expense entries. Try again.", "danger");
      } finally {
        self.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.dropdown-with-zindex {
  z-index: 1050;
}

.action-buttons .dropdown-with-zindex >>> .dropdown-menu {
  z-index: 1060 !important;
}


/* ============================================================= */
/* Brand redesign — navy #1E3A5F / emerald #10B981               */
/* ============================================================= */
h1 { color: #1e3a5f; letter-spacing: -0.01em; }

/* Buttons → brand */
::v-deep .btn-primary {
  background-color: #10b981;
  border-color: #10b981;
}
::v-deep .btn-primary:hover,
::v-deep .btn-primary:focus,
::v-deep .btn-primary:not(:disabled):not(.disabled):active {
  background-color: #059669;
  border-color: #059669;
}
::v-deep .btn-outline-primary {
  color: #1e3a5f;
  border-color: #1e3a5f;
}
::v-deep .btn-outline-primary:hover,
::v-deep .btn-outline-primary.active,
::v-deep .btn-outline-primary:not(:disabled):not(.disabled).active {
  background-color: #1e3a5f;
  border-color: #1e3a5f;
  color: #fff;
}
::v-deep .badge-success { background-color: #10b981; }

/* KPI tiles */
.inv-kpi {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.1rem 1.15rem;
  height: 100%;
}
.inv-kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
.inv-kpi-label { font-size: 0.8rem; font-weight: 600; color: #64748b; }
.inv-kpi-chip { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; font-size: 0.95rem; }
.chip-navy { background: rgba(30, 58, 95, 0.1); color: #1e3a5f; }
.chip-success { background: rgba(16, 185, 129, 0.13); color: #059669; }
.chip-warning { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.chip-danger { background: rgba(239, 68, 68, 0.11); color: #ef4444; }
.inv-kpi-value { font-size: 1.5rem; font-weight: 800; color: #1e3a5f; line-height: 1.1; letter-spacing: -0.02em; }
.inv-kpi-sub { font-size: 0.74rem; color: #94a3b8; margin-top: 0.35rem; }
.inv-kpi-sub.trend-up { color: #059669; }
.inv-kpi-sub.trend-down { color: #ef4444; }

.text-brand-navy { color: #1e3a5f !important; }
.text-success-strong { color: #059669 !important; }
.text-danger-strong { color: #ef4444 !important; }
.text-warn { color: #b45309 !important; }

/* Table polish */
::v-deep .table thead th {
  color: #64748b;
  border-top: none;
  border-bottom: 1px solid #e2e8f0;
}
::v-deep .table tbody td { vertical-align: middle; }
::v-deep .table.table-hover tbody tr:hover { background: #f8fafc; }
</style>
